import { useState } from 'react';
import { SearchCheck, CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useCaeRequests, useConsultarEnArca, useBuscarPorComprobante, useConsultarComprobanteArca } from '../hooks/useCaeRequests.js';
import PageHeader from '../components/PageHeader.jsx';
import Table from '../components/Table.jsx';
import Badge from '../components/Badge.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Modal from '../components/Modal.jsx';

const STATUS_VARIANT = {
  approved: 'success',
  approved_with_observations: 'warning',
  rejected: 'danger',
  error: 'danger',
  pending: 'neutral',
};

// Catálogo de CondicionIVAReceptorId (FEParamGetCondicionIvaReceptor) --
// mismos códigos que src/constants/condicion-iva.js en arca-api/arca-admin-api.
const CONDICION_IVA_RECEPTOR_LABEL = {
  1: 'Responsable Inscripto',
  4: 'Exento',
  5: 'Consumidor Final',
  6: 'Monotributo',
  7: 'Sujeto No Categorizado',
  8: 'Proveedor del Exterior',
  9: 'Cliente del Exterior',
  10: 'IVA Liberado Ley 19640',
  13: 'Monotributista Social',
  15: 'IVA No Alcanzado',
};

function formatCondicionIvaReceptor(id) {
  if (id == null) return '—';
  const label = CONDICION_IVA_RECEPTOR_LABEL[id];
  return label ? `${id} - ${label}` : String(id);
}

// Catálogo de CbteTipo (FEParamGetTiposCbte) -- mismos códigos que
// src/constants/comprobantes.js en arca-api. Cada letra tiene su propio
// color fijo para que se reconozca de un vistazo en toda la tabla/modal.
const TIPOS_CBTE_INFO = {
  1: { nombre: 'Factura', letra: 'A' },
  2: { nombre: 'Nota de Débito', letra: 'A' },
  3: { nombre: 'Nota de Crédito', letra: 'A' },
  6: { nombre: 'Factura', letra: 'B' },
  7: { nombre: 'Nota de Débito', letra: 'B' },
  8: { nombre: 'Nota de Crédito', letra: 'B' },
  11: { nombre: 'Factura', letra: 'C' },
  12: { nombre: 'Nota de Débito', letra: 'C' },
  13: { nombre: 'Nota de Crédito', letra: 'C' },
  51: { nombre: 'Factura', letra: 'M' },
  52: { nombre: 'Nota de Débito', letra: 'M' },
  53: { nombre: 'Nota de Crédito', letra: 'M' },
};

const LETRA_COLOR = {
  A: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  B: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  C: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  M: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
};

function TipoCbteTag({ tipoCbte, compact = false }) {
  const info = TIPOS_CBTE_INFO[Number(tipoCbte)];
  const letra = info?.letra ?? '?';
  const colorClass = LETRA_COLOR[letra] ?? 'bg-bg-elevated text-text-muted border-border';
  if (compact) {
    return (
      <span className={`inline-flex items-center justify-center w-5 h-5 text-[11px] rounded-md border font-bold ${colorClass}`}>
        {letra}
      </span>
    );
  }
  // Versión grande (modal): nombre primero, la letra como tag después.
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-base font-semibold text-text-primary">{info?.nombre ?? `Tipo ${tipoCbte}`}</span>
      <span className={`inline-flex items-center justify-center w-7 h-7 text-sm rounded-md border font-bold ${colorClass}`}>
        {letra}
      </span>
    </span>
  );
}

function ChecklistItem({ ok, children }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-400' : 'text-red-400'}`}>
      {ok ? <CheckCircle2 size={16} className="shrink-0" /> : <XCircle size={16} className="shrink-0" />}
      <span>{children}</span>
    </div>
  );
}

// Aparte del checklist ok/no-ok -- un comprobante puede estar aprobado Y
// tener observaciones de ARCA al mismo tiempo, no es un tercer estado
// binario, así que se marca con su propio tag ámbar en vez de pisar el
// check verde de "Resultado ARCA: Aprobado".
// Botón chico para no romper el layout de 1-2 líneas en la card de
// comprobante asociado -- el componente Button genérico es más alto/ancho
// de lo que entra ahí con el badge al lado.
function MiniButton({ children, ...rest }) {
  return (
    <button
      className="px-2 py-0.5 rounded-md border border-border-strong hover:bg-bg-elevated text-text-secondary hover:text-accent text-[11px] font-medium whitespace-nowrap"
      {...rest}
    >
      {children}
    </button>
  );
}

function ObservadoTag({ cantidad }) {
  return (
    <div className="flex items-center gap-2 text-sm text-amber-400">
      <AlertTriangle size={16} className="shrink-0" />
      <span>Observado por ARCA ({cantidad} {cantidad === 1 ? 'observación' : 'observaciones'})</span>
    </div>
  );
}

// Un solo elemento (objeto) o varios (array) según cuántos haya -- típico de
// fast-xml-parser al no normalizar nodos repetibles con cardinalidad 1.
function toArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

const ALIC_IVA_PCT = { 3: '0%', 9: '2.5%', 8: '5%', 4: '10.5%', 5: '21%', 6: '27%' };

function IvaCards({ iva }) {
  const items = toArray(iva);
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-text-secondary mb-1.5">Desglose de IVA</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it, i) => (
          <div key={i} className="rounded-lg border border-border bg-bg-elevated p-2 text-xs space-y-0.5">
            <p className="font-semibold text-sky-400">{ALIC_IVA_PCT[Number(it.Id)] ?? `Alíc. ${it.Id}`}</p>
            <p className="text-text-muted">Base: ${Number(it.BaseImp ?? 0).toFixed(2)}</p>
            <p className="text-text-muted">IVA: ${Number(it.Importe ?? 0).toFixed(2)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TributosCards({ tributos }) {
  const items = toArray(tributos);
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-text-secondary mb-1.5">Tributos</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it, i) => (
          <div key={i} className="rounded-lg border border-border bg-bg-elevated p-2 text-xs space-y-0.5">
            <p className="font-semibold text-amber-400 truncate" title={it.Desc}>{it.Desc || `Id ${it.Id}`}</p>
            <p className="text-text-muted">Alíc: {Number(it.Alic ?? 0).toFixed(2)}%</p>
            <p className="text-text-muted">Base: ${Number(it.BaseImp ?? 0).toFixed(2)}</p>
            <p className="text-text-muted">Importe: ${Number(it.Importe ?? 0).toFixed(2)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Resaltado tipo markup de JSON sin dependencias -- tokeniza con una sola
// regex (strings/keys, booleanos/null, números) y envuelve cada match en un
// span con color. dangerouslySetInnerHTML es seguro acá porque el input es
// nuestra propia respuesta de ARCA ya escapada, no HTML de un tercero.
function highlightJson(value) {
  const json = escapeHtml(JSON.stringify(value, null, 2));
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'text-amber-400'; // número
      if (/^"/.test(match)) cls = /:$/.test(match) ? 'text-sky-400' : 'text-emerald-400'; // key / string
      else if (/true|false/.test(match)) cls = 'text-violet-400';
      else if (/null/.test(match)) cls = 'text-text-muted';
      return `<span class="${cls}">${match}</span>`;
    },
  );
}

function JsonHighlighted({ value }) {
  return (
    // eslint-disable-next-line react/no-danger
    <pre
      className="input-base font-mono text-xs whitespace-pre overflow-x-auto max-h-64 overflow-y-auto"
      dangerouslySetInnerHTML={{ __html: highlightJson(value) }}
    />
  );
}

// Un comprobante sin fila propia en cae_requests (ej. el asociado de una NC
// es una factura vieja, de antes de este sistema) igual se puede consultar
// en ARCA -- se arma una fila "falsa" con lo mínimo que necesita
// ConsultaResultado (client_id/environment/tipo_cbte) para poder reusar el
// mismo render en el panel lateral sin un id real.
function pseudoRow({ clientId, environment, tipoCbte }) {
  return { id: null, client_id: clientId, environment, tipo_cbte: tipoCbte, cae: null };
}

// Una NC/ND referencia un comprobante original -- primero se busca si ya lo
// tenemos registrado (lectura liviana, se dispara sola). Haya o no fila
// propia, siempre queda la opción de consultarlo en vivo contra ARCA -- una
// cosa no excluye la otra. Los resultados se muestran en el panel lateral
// del modal (onAbrirPanelInterno/onAbrirPanelArca), no acá adentro, para
// poder compararlos lado a lado con el comprobante que se está viendo.
function CbteAsociadoCard({ asoc, row, onAbrirPanelInterno, onAbrirPanelArca }) {
  const tipo = Number(asoc.Tipo);
  const ptoVta = Number(asoc.PtoVta);
  const nro = Number(asoc.Nro ?? asoc.CbteNro);
  const buscar = useBuscarPorComprobante({
    clientId: row.client_id, environment: row.environment, pvNumber: ptoVta, tipoCbte: tipo, cbteNro: nro,
  });

  return (
    <div className="rounded-lg border border-border bg-bg-elevated p-3 text-xs space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-1">
        <div className="flex items-center gap-2">
          <TipoCbteTag tipoCbte={tipo} compact />
          <span className="text-text-secondary">PV {ptoVta} - Nro {nro}</span>
        </div>
        <div className="flex items-center gap-3 text-text-muted">
          {asoc.Cuit && <span>CUIT {asoc.Cuit}</span>}
          {asoc.CbteFch && <span>{asoc.CbteFch}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {buscar.isLoading && <span className="text-text-muted">Buscando...</span>}
        {buscar.isSuccess && (
          <Badge variant={buscar.data ? 'success' : 'neutral'}>
            {buscar.data ? 'Registrado' : 'No registrado'}
          </Badge>
        )}
        {buscar.isSuccess && buscar.data && (
          <MiniButton title="Ver verificación" onClick={() => onAbrirPanelInterno(buscar.data)}>Ver</MiniButton>
        )}
        <MiniButton
          title="Consultar en ARCA"
          onClick={() => onAbrirPanelArca({
            // Si buscar.data ya lo encontró, usamos esa fila real (tiene id
            // y cae) para que el panel pueda hacer el check de "CAE
            // coincide" -- la fila "falsa" (sin id) es SOLO para cuando de
            // verdad no está en nuestros registros.
            pseudoRow: buscar.data ?? pseudoRow({ clientId: row.client_id, environment: row.environment, tipoCbte: tipo }),
            ptoVta, cbteTipo: tipo, cbteNro: nro,
          })}
        >
          Ver ARCA
        </MiniButton>
      </div>
    </div>
  );
}

function CbtesAsociadosSection({ cbtesAsoc, row, onAbrirPanelInterno, onAbrirPanelArca }) {
  const items = toArray(cbtesAsoc);
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-text-secondary mb-1.5">
        Comprobante{items.length > 1 ? 's' : ''} asociado{items.length > 1 ? 's' : ''}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((asoc, i) => (
          <CbteAsociadoCard key={i} asoc={asoc} row={row} onAbrirPanelInterno={onAbrirPanelInterno} onAbrirPanelArca={onAbrirPanelArca} />
        ))}
      </div>
    </div>
  );
}

// Compara lo que ya tenemos guardado contra lo que ARCA acaba de confirmar --
// si no coincide es mucho más grave que un simple "no está", así que se marca
// aparte en vez de mezclarlo con el resto del resultado. row.id == null
// significa que no hay fila propia (ver pseudoRow) -- en ese caso no hay
// nada local con qué comparar, solo se muestra lo que informa ARCA.
function ConsultaResultado({ row, resultado, onAbrirPanelInterno, onAbrirPanelArca }) {
  const tieneRegistroLocal = row.id != null;
  const caeCoincide = tieneRegistroLocal && row.cae === resultado.cae;
  const resultGet = resultado.raw?.ResultGet;
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        {tieneRegistroLocal ? (
          <ChecklistItem ok={caeCoincide}>
            {caeCoincide ? 'El CAE coincide con lo registrado en ARCA' : 'El CAE NO coincide con lo que tenemos'}
          </ChecklistItem>
        ) : (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Info size={16} className="shrink-0" />
            <span>No tenemos este comprobante en nuestros registros -- mostrando solo lo que informa ARCA</span>
          </div>
        )}
        {resultado.resultado && (
          <ChecklistItem ok={resultado.resultado === 'A'}>
            Resultado ARCA: {resultado.resultado === 'A' ? 'Aprobado' : 'Rechazado'}
          </ChecklistItem>
        )}
        {resultado.observaciones?.length > 0 && (
          <ObservadoTag cantidad={resultado.observaciones.length} />
        )}
      </div>
      <TipoCbteTag tipoCbte={row.tipo_cbte} />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-text-muted">CAE (ARCA)</dt><dd className="font-mono">{resultado.cae || '—'}</dd>
        <dt className="text-text-muted">CAE (nuestro)</dt>
        <dd className="font-mono">{tieneRegistroLocal ? (row.cae || '—') : '— (no registrado)'}</dd>
        <dt className="text-text-muted">Vencimiento CAE</dt><dd>{resultado.caeVencimiento || '—'}</dd>
        <dt className="text-text-muted">Fecha comprobante</dt><dd>{resultado.cbteFch || '—'}</dd>
        <dt className="text-text-muted">Importe total</dt><dd>${Number(resultado.impTotal ?? 0).toFixed(2)}</dd>
        <dt className="text-text-muted">Importe neto</dt><dd>${Number(resultado.impNeto ?? 0).toFixed(2)}</dd>
        <dt className="text-text-muted">Importe IVA</dt><dd>${Number(resultado.impIva ?? 0).toFixed(2)}</dd>
        <dt className="text-text-muted">Condición IVA receptor</dt><dd>{formatCondicionIvaReceptor(resultado.condicionIvaReceptorId)}</dd>
      </dl>
      <IvaCards iva={resultGet?.Iva?.AlicIva} />
      <TributosCards tributos={resultGet?.Tributos?.Tributo} />
      <CbtesAsociadosSection
        cbtesAsoc={resultGet?.CbtesAsoc?.CbteAsoc}
        row={row}
        onAbrirPanelInterno={onAbrirPanelInterno}
        onAbrirPanelArca={onAbrirPanelArca}
      />
      {resultado.observaciones?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-text-secondary mb-1">Observaciones de ARCA</p>
          <ul className="text-xs text-text-muted list-disc list-inside">
            {resultado.observaciones.map((o, i) => <li key={i}>({o.code}) {o.msg}</li>)}
          </ul>
        </div>
      )}
      <details>
        <summary className="text-xs text-text-muted cursor-pointer">Ver respuesta completa de ARCA</summary>
        <div className="mt-2">
          <JsonHighlighted value={resultado.raw} />
        </div>
      </details>
    </div>
  );
}

// Lo que ya tenemos guardado en cae_requests para el comprobante asociado --
// sin pegarle a ARCA. cae_requests no guarda el desglose de Iva/Tributos/
// CbtesAsoc (eso solo queda en operation_logs, de diagnóstico), por eso acá
// se muestran solo los campos planos que sí tiene la tabla.
function RegistroLocalPanel({ row }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <TipoCbteTag tipoCbte={row.tipo_cbte} />
        <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-text-muted">CAE</dt><dd className="font-mono">{row.cae || '—'}</dd>
        <dt className="text-text-muted">Vencimiento CAE</dt>
        <dd>{row.cae_vencimiento ? new Date(row.cae_vencimiento).toLocaleDateString('es-AR') : '—'}</dd>
        <dt className="text-text-muted">Fecha comprobante</dt>
        <dd>{row.cbte_fch ? new Date(row.cbte_fch).toLocaleDateString('es-AR') : '—'}</dd>
        <dt className="text-text-muted">Importe total</dt><dd>${Number(row.imp_total ?? 0).toFixed(2)}</dd>
        <dt className="text-text-muted">Importe neto</dt><dd>${Number(row.imp_neto ?? 0).toFixed(2)}</dd>
        <dt className="text-text-muted">Importe IVA</dt><dd>${Number(row.imp_iva ?? 0).toFixed(2)}</dd>
        <dt className="text-text-muted">Importe tributos</dt><dd>${Number(row.imp_trib ?? 0).toFixed(2)}</dd>
        <dt className="text-text-muted">Condición IVA receptor</dt><dd>{formatCondicionIvaReceptor(row.condicion_iva_receptor_id)}</dd>
      </dl>
      {row.observaciones?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-text-secondary mb-1">Observaciones (al momento de emitir)</p>
          <ul className="text-xs text-text-muted list-disc list-inside">
            {row.observaciones.map((o, i) => <li key={i}>({o.code}) {o.msg}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CaeRequests() {
  const [status, setStatus] = useState('');
  const [consultaRow, setConsultaRow] = useState(null);
  // Panel lateral: el comprobante asociado se muestra al lado del principal
  // para comparar, no reemplazándolo. panelMode 'interno' es instantáneo (ya
  // tenemos la fila, no pega a ARCA); 'arca' dispara la consulta en vivo.
  const [panelRow, setPanelRow] = useState(null);
  const [panelMode, setPanelMode] = useState(null); // 'interno' | 'arca' | null
  const { data, isLoading } = useCaeRequests(status ? { status } : {});
  const consultar = useConsultarEnArca();
  const panelArca = useConsultarComprobanteArca();

  const handleConsultar = (row) => {
    setConsultaRow(row);
    setPanelRow(null);
    setPanelMode(null);
    consultar.mutate(row.id);
  };

  const closeModal = () => {
    setConsultaRow(null);
    setPanelRow(null);
    setPanelMode(null);
    consultar.reset();
    panelArca.reset();
  };

  const abrirPanelInterno = (foundRow) => {
    setPanelRow(foundRow);
    setPanelMode('interno');
    panelArca.reset();
  };

  const abrirPanelArca = ({ pseudoRow: row, ptoVta, cbteTipo, cbteNro }) => {
    setPanelRow(row);
    setPanelMode('arca');
    panelArca.mutate({ clientId: row.client_id, environment: row.environment, ptoVta, cbteTipo, cbteNro });
  };

  const cerrarPanel = () => {
    setPanelRow(null);
    setPanelMode(null);
    panelArca.reset();
  };

  return (
    <div>
      <PageHeader
        title="Comprobantes"
        subtitle="Pedidos de CAE emitidos contra ARCA"
        actions={
          <Input as="select" value={status} onChange={(e) => setStatus(e.target.value)} className="w-48">
            <option value="">Todos los estados</option>
            <option value="approved">Aprobado</option>
            <option value="approved_with_observations">Aprobado con obs.</option>
            <option value="rejected">Rechazado</option>
            <option value="error">Error</option>
          </Input>
        }
      />
      <div className="card">
        <Table
          loading={isLoading}
          data={data}
          emptyMessage="Sin comprobantes registrados"
          columns={[
            { key: 'created_at', label: 'Fecha', render: (r) => new Date(r.created_at).toLocaleString('es-AR') },
            { key: 'client_name', label: 'Cliente' },
            { key: 'pv_number', label: 'PV' },
            { key: 'tipo_cbte', label: 'Tipo', render: (r) => <TipoCbteTag tipoCbte={r.tipo_cbte} compact /> },
            { key: 'cbte_desde', label: 'Nro' },
            { key: 'cae', label: 'CAE', render: (r) => r.cae || '—' },
            { key: 'imp_total', label: 'Total', render: (r) => `$${Number(r.imp_total).toFixed(2)}` },
            { key: 'status', label: 'Estado', render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge> },
            {
              key: 'actions', label: '', render: (r) => (
                <button
                  onClick={() => handleConsultar(r)}
                  className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-accent"
                  title="Verificar en ARCA"
                >
                  <SearchCheck size={15} />
                </button>
              ),
            },
          ]}
        />
      </div>
      <Modal open={!!consultaRow} onClose={closeModal} title="Verificación contra ARCA" maxWidth={panelRow ? 'max-w-5xl' : 'max-w-2xl'}>
        {consultaRow && (
          <div className={`grid gap-4 ${panelRow ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
            <div>
              {panelRow && <p className="text-xs font-medium text-text-secondary mb-2">Comprobante consultado</p>}
              {consultar.isPending && (
                <div className="flex items-center gap-2 text-sm text-text-muted">
                  <Button loading disabled variant="ghost">Consultando ARCA...</Button>
                </div>
              )}
              {consultar.isError && (
                <p className="text-sm text-red-400">
                  {consultar.error?.response?.data?.error || 'No se pudo consultar el comprobante en ARCA.'}
                </p>
              )}
              {consultar.isSuccess && (
                <ConsultaResultado row={consultaRow} resultado={consultar.data} onAbrirPanelInterno={abrirPanelInterno} onAbrirPanelArca={abrirPanelArca} />
              )}
            </div>
            {panelRow && (
              <div className="border-t lg:border-t-0 lg:border-l border-border pt-4 lg:pt-0 lg:pl-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-text-secondary">
                    Comprobante asociado ({panelMode === 'interno' ? 'registro local' : 'ARCA'})
                  </p>
                  <button onClick={cerrarPanel} className="text-text-muted hover:text-text-primary" title="Cerrar comparación">
                    <X size={14} />
                  </button>
                </div>
                {panelMode === 'interno' && <RegistroLocalPanel row={panelRow} />}
                {panelMode === 'arca' && (
                  <>
                    {panelArca.isPending && (
                      <div className="flex items-center gap-2 text-sm text-text-muted">
                        <Button loading disabled variant="ghost">Consultando ARCA...</Button>
                      </div>
                    )}
                    {panelArca.isError && (
                      <p className="text-sm text-red-400">
                        {panelArca.error?.response?.data?.error || 'No se pudo consultar el comprobante en ARCA.'}
                      </p>
                    )}
                    {panelArca.isSuccess && (
                      <ConsultaResultado row={panelRow} resultado={panelArca.data} onAbrirPanelInterno={abrirPanelInterno} onAbrirPanelArca={abrirPanelArca} />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
